import React, { useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { uploadDocumentFile } from '../../firebase/storageService';
import { DOCUMENT_TYPES, documentState, daysUntilExpiry } from '../../utils/driverDocuments';
import { COLORS } from '../../styles/tokens';
import { sheetButtons } from '../common/SheetModal';

const STATE_CHIPS = {
  approved: { label: 'Godkjent',              style: { background: COLORS.successBg, color: COLORS.successText } },
  pending:  { label: 'Venter på godkjenning', style: { background: COLORS.blueBg,    color: COLORS.blueText } },
  missing:  { label: 'Mangler',               style: { background: COLORS.warningBg, color: COLORS.warningText } },
  rejected: { label: 'Avvist',                style: { background: COLORS.dangerBg,  color: COLORS.dangerText } },
  expired:  { label: 'Utløpt',                style: { background: COLORS.dangerBg,  color: COLORS.dangerText } },
};

function StateInfo({ doc }) {
  const state = documentState(doc);
  const chip = STATE_CHIPS[state];
  return (
    <div style={{ marginBottom: 8 }}>
      <span style={{ ...styles.chip, ...chip.style }}>{chip.label}</span>
      {doc?.expiresOn && <span style={styles.meta}> Utløper {doc.expiresOn}</span>}
      {Array.isArray(doc?.files) && doc.files.length > 0 && (
        <div style={styles.meta}>{doc.files.map(f => f.name).join(', ')}</div>
      )}
      {state === 'rejected' && doc.rejectReason && (
        <p style={{ ...styles.meta, color: COLORS.dangerText }}>Avvist: {doc.rejectReason}</p>
      )}
    </div>
  );
}

// Ett skjema: filer, dato (og navn for kurs) som sendes til submitDriverDocument.
function UploadForm({ docKey, courseId, uid, requiresDate, isCourse, submitLabel, onChanged }) {
  const [files, setFiles] = useState([]);
  const [name, setName] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const submitting = useRef(false);

  async function submit(e) {
    e.preventDefault();
    if (submitting.current) return;
    if (files.length === 0) return setError('Velg minst én fil.');
    if (isCourse && !name.trim()) return setError('Skriv et navn på kurset.');
    if (requiresDate && !expiresOn) return setError('Fyll ut utløpsdato.');
    if (expiresOn && daysUntilExpiry({ expiresOn }) < 0) return setError('Datoen kan ikke være i fortiden.');
    submitting.current = true;
    setBusy(true); setError('');
    try {
      const uploaded = [];
      for (const file of files) uploaded.push(await uploadDocumentFile(file, uid, docKey));
      const payload = { docKey, files: uploaded };
      if (courseId) payload.courseId = courseId;
      if (isCourse) payload.name = name.trim();
      if (expiresOn) payload.expiresOn = expiresOn;
      await httpsCallable(functions, 'submitDriverDocument')(payload);
      setFiles([]); setName(''); setExpiresOn(''); setFormKey(k => k + 1);
      onChanged?.();
    } catch {
      setError('Kunne ikke sende inn. Prøv igjen.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {isCourse && (
        <label style={styles.field}>
          Kursnavn
          <input type="text" value={name} maxLength={80} style={styles.input} onChange={e => setName(e.target.value)} />
        </label>
      )}
      <label style={styles.field}>
        Filer (bilde eller PDF)
        <input key={formKey} type="file" accept="image/*,application/pdf" multiple style={styles.input}
               onChange={e => setFiles(Array.from(e.target.files ?? []))} />
      </label>
      <label style={styles.field}>
        {requiresDate ? 'Utløpsdato' : 'Utløpsdato (valgfri)'}
        <input type="date" value={expiresOn} style={styles.input} onChange={e => setExpiresOn(e.target.value)} />
      </label>
      {error && <p role="alert" style={styles.error}>{error}</p>}
      <button type="submit" style={styles.submit} disabled={busy}>{busy ? 'Sender…' : submitLabel}</button>
    </form>
  );
}

// Bunnark-innhold: sjåførens dokumenter med tilstand og opplasting.
export default function DriverDocuments({ userDoc, uid, onChanged, onClose }) {
  const documents = userDoc?.documents ?? {};
  const courses = Array.isArray(documents.courses) ? documents.courses : [];

  return (
    <div>
      <h2 style={sheetButtons.title}>Dokumenter</h2>
      <p style={sheetButtons.text}>Last opp dokumentene dine. Tippi kontrollerer dem og gir beskjed når de er behandlet.</p>

      {Object.entries(DOCUMENT_TYPES).map(([key, type]) => {
        const doc = documents[key];
        const state = documentState(doc);
        return (
          <section key={key} aria-label={type.label} style={styles.section}>
            <h3 style={styles.heading}>{type.label}</h3>
            <StateInfo doc={doc} />
            <UploadForm docKey={key} uid={uid} requiresDate={type.requiresDate}
                        submitLabel={state === 'missing' ? 'Send inn' : 'Last opp på nytt'} onChanged={onChanged} />
          </section>
        );
      })}

      <section aria-label="Kurs og sertifikater" style={styles.section}>
        <h3 style={styles.heading}>Kurs og sertifikater</h3>
        {courses.map(c => (
          <div key={c.id} style={{ marginBottom: 6 }}>
            <strong style={{ fontSize: 14, color: COLORS.textDark }}>{c.name}</strong>
            <StateInfo doc={c} />
          </div>
        ))}
        <UploadForm docKey="course" uid={uid} requiresDate={false} isCourse submitLabel="Legg til kurs" onChanged={onChanged} />
      </section>

      <button type="button" style={sheetButtons.ghost} onClick={onClose}>Lukk</button>
    </div>
  );
}

const styles = {
  section: { padding: '14px 0', borderBottom: `1px solid ${COLORS.border}` },
  heading: { fontWeight: 700, fontSize: 15, color: COLORS.textDark, margin: '0 0 8px' },
  chip:    { borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },
  meta:    { fontSize: 13, color: COLORS.textMid, lineHeight: 1.45, margin: '4px 0 0' },
  field:   { display: 'grid', gap: 4, fontSize: 13, color: COLORS.textMid, marginBottom: 10 },
  input:   { fontSize: 15, color: COLORS.textDark, minHeight: 40 },
  error:   { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '8px 12px', borderRadius: 10, fontSize: 13, margin: '0 0 10px' },
  submit:  { height: 44, padding: '0 20px', borderRadius: 22, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};
